// Store-level team gender. `both` means the record is for boys and girls
// together — it does NOT mint a second Shopify store. Create still builds one
// collection. Read gender, then sport_gender, then a sport-id suffix.

export const STORE_GENDERS = ['boys', 'girls', 'coed', 'both'] as const;
export type StoreGender = (typeof STORE_GENDERS)[number];

export const BOTH_GENDER_NOTE =
  'gender=both is one store. Create does not mint a second boys/girls Shopify store.';

const GENDER_ALIASES: Record<string, StoreGender> = {
  boys: 'boys',
  boy: 'boys',
  men: 'boys',
  mens: 'boys',
  male: 'boys',
  girls: 'girls',
  girl: 'girls',
  women: 'girls',
  womens: 'girls',
  female: 'girls',
  coed: 'coed',
  'co-ed': 'coed',
  co_ed: 'coed',
  both: 'both',
};

export function mapGenderToken(token?: string | null): StoreGender | '' {
  const t = String(token || '').trim().toLowerCase();
  return GENDER_ALIASES[t] || '';
}

export function inferGenderFromSport(sport?: string | null): StoreGender | '' {
  const parts = String(sport || '').toLowerCase().split(/[\s_-]+/).filter(Boolean);
  if (!parts.length) return '';
  const last = mapGenderToken(parts[parts.length - 1]);
  if (last) return last;
  for (const part of parts) {
    const g = mapGenderToken(part);
    if (g) return g;
  }
  return '';
}

/**
 * Football_male / football_boys → { sport: 'football', gender: 'boys' }.
 *
 * Mirrors lib/teamstore-wizard.js splitSportAndGender on the server. The
 * gender token is stripped WHEREVER it sits, not assumed to be last: Scout and
 * operators both write "Boys Basketball" as often as "basketball_boys", and
 * stripping the trailing token there left sport "Boys" (and, once a sport_id
 * is derived from it, "boys_boys"). Scan backwards so a trailing token still
 * wins when a name carries two candidates, then rejoin with single spaces.
 * Parentheses are treated as whitespace for tokenising only, so
 * "Basketball (Boys)" splits — a name with no gender token keeps its original
 * text, so "Volleyball (JV)" survives intact.
 */
export function splitSportAndGender(sport?: string | null): { sport: string; gender: StoreGender | '' } {
  const raw = String(sport || '').trim();
  const parts = raw.replace(/[()]/g, ' ').split(/[\s_-]+/).filter(Boolean);
  let idx = -1;
  let gender: StoreGender | '' = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    const g = mapGenderToken(parts[i]);
    if (g) {
      idx = i;
      gender = g;
      break;
    }
  }
  if (!gender) return { sport: raw, gender: '' };
  const stripped = parts.filter((_, i) => i !== idx).join(' ');
  return { sport: stripped || raw, gender };
}

export function resolveStoreGender(s: {
  gender?: string | null;
  sport_gender?: string | null;
  sport_id?: string | null;
  sportType?: string | null;
}): string | null {
  const mapped = mapGenderToken(s.gender) || mapGenderToken(s.sport_gender);
  if (mapped) return mapped;
  const raw = String(s.gender || s.sport_gender || '').trim().toLowerCase();
  if (raw) return raw;
  return inferGenderFromSport(s.sport_id || s.sportType) || null;
}

export function genderLabel(g?: string | null): string {
  if (!g) return '—';
  return mapGenderToken(g) || g;
}

export function isBothGender(g?: string | null): boolean {
  return mapGenderToken(g) === 'both' || String(g || '').trim().toLowerCase() === 'both';
}
